import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, effect, inject, signal } from '@angular/core';

export interface AccessibilityPreferences {
  textSize: 'default' | 'large' | 'extra-large';
  highContrast: boolean;
  reduceMotion: boolean;
  focusMode: boolean;
}

const DEFAULTS: AccessibilityPreferences = {
  textSize: 'default',
  highContrast: false,
  reduceMotion: false,
  focusMode: false,
};

@Injectable({ providedIn: 'root' })
export class AccessibilityPreferencesService {
  private readonly document = inject(DOCUMENT);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly preferences = signal<AccessibilityPreferences>(this.read());

  constructor() {
    effect(() => {
      if (!this.browser || !this.document.documentElement) return;
      const value = this.preferences();
      const root = this.document.documentElement;
      root.dataset['textSize'] = value.textSize;
      root.classList.toggle('ds-high-contrast', value.highContrast);
      root.classList.toggle('ds-reduce-motion', value.reduceMotion);
      root.classList.toggle('ds-focus-mode', value.focusMode);
      if (typeof localStorage !== 'undefined')
        localStorage.setItem('iqlabtest-accessibility', JSON.stringify(value));
    });
  }

  update(patch: Partial<AccessibilityPreferences>): void {
    this.preferences.update((value) => ({ ...value, ...patch }));
  }
  reset(): void {
    this.preferences.set(DEFAULTS);
  }
  private read(): AccessibilityPreferences {
    if (!this.browser || typeof localStorage === 'undefined') return DEFAULTS;
    try {
      return {
        ...DEFAULTS,
        ...JSON.parse(localStorage.getItem('iqlabtest-accessibility') ?? '{}'),
      };
    } catch {
      return DEFAULTS;
    }
  }
}
